//#region IMPORTS
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { Order, ORDERSTATUS, ORDERTYPE } from "../../models/order";
import {
  CHOOSE_PICKLIST_SUPPLIER,
  CONFIRM_BULK_ORDERS,
  BULK_CONFIRM_THRESHOLD,
} from "../../models/_consts";
import { Fragment, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import useBulkActionsAvailable from "../../hooks/useBulkActionsAvailable";
import useReadOnlyPreset from "../../hooks/useReadOnlyPreset";
import {
  fetchOrdersAsync,
  fetchApprovableOrdersAsync,
  fetchShippableOrdersAsync,
  fetchInProgressOrdersAsync,
} from "../../features/orders/ordersSlice";
import { openModal } from "../../features/dashBoard/dashboardSlice";
import { SubOrder } from "../../models/suborder";
import { Tooltip } from "react-tooltip";
import { toast } from "react-toastify";
import MainModal from "../_layout/MainModal";
import AreYouSure from "../modalTypes/AreYouSure";
import agent from "../../api/agent";
//#endregion

//#region INTERFACE
interface Props {
  hideHandle?: boolean;
  isHQTeam: boolean;
  isPicker: boolean;
  isAdmin: boolean;
}
//#endregion

export default function OrdersTableControlePanelBulkActions({
  hideHandle = false,
  isHQTeam,
  isPicker,
  isAdmin,
}: Props) {
  //#region SETUP
  // Redux
  const dispatch = useAppDispatch();
  const { search } = useLocation();
  const preset = new URLSearchParams(search).get("preset");
  const readOnly = useReadOnlyPreset();
  const { selectedOrders, order_params } = useAppSelector(
    (state) => state.orders,
  );
  const isInProgressFilter =
    order_params.order_status === ORDERSTATUS.inProgress;
  const hasPromoCode =
    typeof order_params.promo_code === "string" &&
    order_params.promo_code !== "";

  // Refetch the list + the flow counts after a bulk action. The list refetch
  // flips orders_loaded, which lets PromoOrdersView auto-advance if it emptied.
  const refreshAfterBulk = async () => {
    await Promise.all([
      dispatch(fetchOrdersAsync()),
      dispatch(fetchApprovableOrdersAsync()),
      dispatch(fetchShippableOrdersAsync()),
      dispatch(fetchInProgressOrdersAsync()),
    ]);
  };
  // Each flow preset locks one status, so we surface only the matching primary
  // action (mirrors the instore flow).
  const statusFilter = order_params.order_status;
  // Requesting lists is per promo: needs backorder + a selected promo code
  // (a status filter is optional).
  const requestListsReady =
    order_params.order_type === ORDERTYPE.backorder && hasPromoCode;
  // The "Request list(s)" button is shown (with a how-to tooltip) on the
  // backorder contexts where it applies — Backorder / Manage / Send.
  const showRequestLists =
    (isAdmin || isPicker) && !readOnly && preset !== "approve";

  // Local state
  const [approveAbleOrders, setApproveAbleOrders] = useState<Order[]>([]);
  const [shippableOrders, setShippableOrders] = useState<Order[]>([]);
  const [inProgressableOrders, setInProgressableOrders] = useState<Order[]>([]);

  // Confirmation modal for the bulk actions
  const [pendingAction, setPendingAction] = useState<{
    message: string;
    run: () => void;
  } | null>(null);

  // Only ask for confirmation on larger batches; small ones run immediately.
  const requestConfirm = (count: number, message: string, run: () => void) => {
    if (count > BULK_CONFIRM_THRESHOLD) {
      setPendingAction({ message, run });
      dispatch(openModal(CONFIRM_BULK_ORDERS));
    } else {
      run();
    }
  };

  const closeConfirm = () => {
    setPendingAction(null);
    dispatch(openModal(false));
  };

  // Bulk fulfilment (ship + picklists) is only offered once the list is
  // filtered down to a single order status (and never on Original / All).
  const bulkAvailable = useBulkActionsAvailable();
  //#endregion

  //#region LOGIC
  // Select orders by purpose
  useEffect(() => {
    // Shipping is now the step after "in progress": Ordered → In progress →
    // Shipped. Only in-progress orders can be shipped in bulk.
    setShippableOrders(
      selectedOrders.filter(
        (order: Order) =>
          order.order_status === ORDERSTATUS.inProgress &&
          order.order_type !== ORDERTYPE.original,
      ),
    );
    setApproveAbleOrders(
      selectedOrders.filter(
        (order: Order) =>
          order.order_status === ORDERSTATUS.pending &&
          order.order_type !== ORDERTYPE.original,
      ),
    );
    setInProgressableOrders(
      selectedOrders.filter(
        (order: Order) =>
          order.order_status === ORDERSTATUS.ordered &&
          order.order_type !== ORDERTYPE.original,
      ),
    );
  }, [selectedOrders]);

  // Approve in bulk
  const bulkApproveHandler = async () => {
    const updateOrderValues = approveAbleOrders.map((apo) => {
      return {
        id: apo.id,
        approved: apo.approved,
      };
    });

    try {
      const ammountApproved: number = updateOrderValues.filter(
        (o) => o.approved,
      ).length;
      const ammountDeclined: number = updateOrderValues.filter(
        (o) => !o.approved,
      ).length;

      // Approve orders main action
      const response = await agent.Orders.approveOrder(updateOrderValues);

      // Approve bulk suborders of the orders
      const approveAbles =
        approveAbleOrders
          .filter((order) => order.order_status === ORDERSTATUS.ordered)
          .map((order) => order.sub_orders)
          .flat() ?? [];

      approveAbles.length > 0 &&
        (await updateBulkSuborders(approveAbles, ORDERSTATUS.ordered));

      // Decline bulk suborders of the orders
      const declinables =
        approveAbleOrders
          .filter((order) => order.order_status === ORDERSTATUS.cancelled)
          .map((order) => order.sub_orders)
          .flat() ?? [];

      declinables.length > 0 &&
        (await updateBulkSuborders(declinables, ORDERSTATUS.cancelled));

      //#region SHOW TOAST RESPONSE
      Object.values(response.result).length - ammountDeclined > 0 &&
        toast.success(
          `${Object.values(response.result).length - ammountDeclined} order${
            Object.values(response.result).length - ammountDeclined > 1
              ? "s"
              : ""
          } approved`,
        );
      Object.values(response.result).length - ammountApproved > 0 &&
        toast.error(
          `${Object.values(response.result).length - ammountApproved} order${
            Object.values(response.result).length - ammountApproved > 1
              ? "s"
              : ""
          } declined`,
        );
      //#endregion
    } catch (error) {
      toast.error(`${error}`);
    }
    await refreshAfterBulk();
  };

  // Ship in bulk
  const bulkShipHandler = async () => {
    try {
      // Update statusses of the orders
      const updateOrderValues = {
        orders: shippableOrders.map((order) => order.id.toString()),
        order_status: ORDERSTATUS.shipped,
      };
      const response =
        await agent.Orders.updateMultiOrderStatus(updateOrderValues);

      // Update statusses of suborders of the orders
      await updateBulkSuborders(
        shippableOrders.map((order) => order.sub_orders).flat(),
        ORDERSTATUS.shipped,
      );

      // Show message
      toast.success(
        `${response.data.length} order${
          response.data.length > 1 ? "s" : ""
        } succesfully changed to shipped `,
      );
    } catch (error: any) {
      toast.error(error.message);
    }
    await refreshAfterBulk();
  };

  // Set in progress in bulk
  const bulkInProgressHandler = async () => {
    try {
      const updateOrderValues = {
        orders: inProgressableOrders.map((order) => order.id.toString()),
        order_status: ORDERSTATUS.inProgress,
      };
      const response =
        await agent.Orders.updateMultiOrderStatus(updateOrderValues);

      await updateBulkSuborders(
        inProgressableOrders.map((order) => order.sub_orders).flat(),
        ORDERSTATUS.inProgress,
      );

      toast.success(
        `${response.data.length} order${
          response.data.length > 1 ? "s" : ""
        } succesfully set in progress `,
      );
    } catch (error: any) {
      toast.error(error.message);
    }
    await refreshAfterBulk();
  };

  // Update suborder helper
  const updateBulkSuborders = async (
    subOrders: SubOrder[],
    order_status: string,
  ) => {
    const updateSuborderValues = {
      sub_orders: subOrders.map((subOrder) => subOrder.id.toString()),
      order_status,
    };

    // Check if suborders can be changed
    if (
      subOrders.some(
        (subOrder) => subOrder.order_status === ORDERSTATUS.shipped,
      )
    ) {
      toast.warning("Suborders that are shipped can not be changed anymore");
    } else {
      await agent.Orders.updateMultiSubOrderStatus(updateSuborderValues);
    }
  };
  //#endregion

  //#region RENDER
  return (
    <div className="flex gap-4 items-center">
      {/* Request list(s) — per promo (backorder + status + promo code) */}
      {showRequestLists && (
        <Fragment>
          <button
            onClick={() => dispatch(openModal(CHOOSE_PICKLIST_SUPPLIER))}
            disabled={!requestListsReady || selectedOrders.length <= 0}
            type="button"
            // Only hint how to enable it while it's disabled — once active the
            // how-to tooltip is unnecessary.
            {...(requestListsReady && selectedOrders.length > 0
              ? {}
              : {
                  "data-tooltip-id": "request-lists-help",
                  "data-tooltip-content":
                    "Filter on a promo code (optionally a status), then select orders to request their lists.",
                  "data-tooltip-place": "bottom",
                })}
            className="inline-flex items-center rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white"
          >
            {requestListsReady && selectedOrders.length > 0
              ? `Request ${selectedOrders.length} list${
                  selectedOrders.length > 1 ? "s" : ""
                }`
              : "Request list(s)"}
          </button>
          <Tooltip id="request-lists-help" />
        </Fragment>
      )}

      {/* Bulk set in progress */}
      {(isPicker || isAdmin) &&
        bulkAvailable &&
        statusFilter === ORDERSTATUS.ordered && (
        <button
          onClick={() =>
            requestConfirm(
              inProgressableOrders.length,
              `Set ${inProgressableOrders.length} order${
                inProgressableOrders.length > 1 ? "s" : ""
              } in progress?`,
              bulkInProgressHandler,
            )
          }
          disabled={inProgressableOrders.length <= 0 || isInProgressFilter}
          type="button"
          className="inline-flex items-center rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white"
        >
          {inProgressableOrders.length > 0
            ? `Set ${inProgressableOrders.length} in progress`
            : "Set in progress"}
        </button>
      )}

      {/* Bulk ship order */}
      {(isPicker || isAdmin) &&
        bulkAvailable &&
        statusFilter === ORDERSTATUS.inProgress && (
        <button
          onClick={() =>
            requestConfirm(
              shippableOrders.length,
              `Ship ${shippableOrders.length} order${
                shippableOrders.length > 1 ? "s" : ""
              }?`,
              bulkShipHandler,
            )
          }
          disabled={shippableOrders.length <= 0}
          type="button"
          className="inline-flex items-center rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white"
        >
          {shippableOrders.length > 0
            ? `Ship ${shippableOrders.length} order${
                shippableOrders.length > 1 ? "s" : ""
              }`
            : "Ship orders"}
        </button>
      )}

      {/* Bulk approve or decline orders — HQ / admin only (not pickers) */}
      {(isHQTeam || isAdmin) &&
        !hideHandle &&
        bulkAvailable &&
        statusFilter === ORDERSTATUS.pending && (
        <button
          onClick={() =>
            requestConfirm(
              approveAbleOrders.length,
              `Process ${approveAbleOrders.length} order${
                approveAbleOrders.length > 1 ? "s" : ""
              }?`,
              bulkApproveHandler,
            )
          }
          disabled={approveAbleOrders.length <= 0}
          type="button"
          className="inline-flex items-center rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white"
        >
          {approveAbleOrders.length > 0
            ? `Approve ${approveAbleOrders.length} order${
                approveAbleOrders.length > 1 ? "s" : ""
              }`
            : "Approve multiple"}
        </button>
      )}

      {/* Confirmation modal for the bulk actions */}
      <MainModal type={CONFIRM_BULK_ORDERS} closeHandler={closeConfirm}>
        <AreYouSure
          closeHandler={closeConfirm}
          confirmHandler={() => {
            pendingAction?.run();
            closeConfirm();
          }}
          title="Confirm bulk action"
          message={pendingAction?.message}
        />
      </MainModal>
    </div>
  );
  //#endregion
}
